// v232 Cross-Instance Geo-Sync admin API client (item 48 of
// docs/PRODUCT-ROADMAP.md, item 59 in delivery order — Phases A + B + C).
// Backs the AdminGeo page. Surfaces: master-toggle settings, peer
// CRUD with per-peer bandwidth + pull-cadence knobs, per-peer "Test
// connection" / "Pause" / "Resume" / "Flip direction" failover-switch
// actions (Phase C), recent outbox tail, and the Phase B apply-audit
// tail (per-event applied / skipped / conflict / loop / error decisions
// from the receive side).

import useSWR from 'swr';

export interface GeoSettings {
  enabled: boolean;
  instance_id: string;
  outbox_retention_days: number;
  push_interval_sec: number;
  updated_at: string;
  updated_by?: string;
}

export interface GeoPeer {
  id: string;
  name: string;
  endpoint_url: string;
  direction: 'push' | 'pull' | 'bidir';
  enabled: boolean;
  has_token: boolean;
  bandwidth_kbps: number;
  pull_interval_sec: number;
  pull_batch_size: number;
  created_at: string;
  updated_at: string;
  last_pushed_id: number;
  last_pushed_at?: string | null;
  last_error: string;
  last_error_at?: string | null;
  consecutive_failures: number;
  last_pulled_id: number;
  last_pulled_at?: string | null;
  last_pull_error: string;
  last_pull_error_at?: string | null;
  consecutive_pull_failures: number;
  bytes_pushed: number;
  bytes_pulled: number;
}

export interface GeoPeerInput {
  name: string;
  endpoint_url: string;
  bearer_token?: string;
  direction: 'push' | 'pull' | 'bidir';
  enabled: boolean;
  bandwidth_kbps: number;
  pull_interval_sec: number;
  pull_batch_size: number;
}

export interface GeoOutboxRow {
  id: number;
  event_type: string;
  scope_id: string;
  occurred_at: string;
  payload: Record<string, unknown>;
}

export interface GeoApplyAuditRow {
  id: number;
  source_instance: string;
  direction: 'inbound_push' | 'inbound_pull';
  peer_id?: string;
  event_type: string;
  event_id: number;
  scope_id: string;
  decision: 'applied' | 'skipped' | 'conflict' | 'loop' | 'error';
  reason: string;
  applied_at: string;
}

interface PeerListResponse {
  rows: GeoPeer[];
}

interface OutboxListResponse {
  rows: GeoOutboxRow[];
}

interface ApplyAuditListResponse {
  rows: GeoApplyAuditRow[];
}

const json = (url: string) =>
  fetch(url, { credentials: 'include' }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  });

export function useGeoSettings() {
  return useSWR<GeoSettings>('/api/admin/geo/settings', json, {
    refreshInterval: 15_000,
  });
}

export function useGeoPeers() {
  return useSWR<PeerListResponse>('/api/admin/geo/peers', json, {
    refreshInterval: 15_000,
  });
}

export function useGeoOutbox() {
  return useSWR<OutboxListResponse>('/api/admin/geo/outbox', json, {
    refreshInterval: 30_000,
  });
}

export function useGeoApplyAudit() {
  return useSWR<ApplyAuditListResponse>('/api/admin/geo/apply-audit', json, {
    refreshInterval: 30_000,
  });
}

export async function updateGeoSettings(
  input: Pick<
    GeoSettings,
    'enabled' | 'instance_id' | 'outbox_retention_days' | 'push_interval_sec'
  >,
): Promise<void> {
  const res = await fetch('/api/admin/geo/settings', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
}

export async function createGeoPeer(input: GeoPeerInput): Promise<{ id: string }> {
  const res = await fetch('/api/admin/geo/peers', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
}

export async function updateGeoPeer(id: string, input: GeoPeerInput): Promise<void> {
  const res = await fetch(`/api/admin/geo/peers/${encodeURIComponent(id)}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
}

export async function deleteGeoPeer(id: string): Promise<void> {
  const res = await fetch(`/api/admin/geo/peers/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) throw new Error(`${res.status}: ${await res.text()}`);
}

export interface GeoTestResult {
  ok: boolean;
  status?: string;
  error?: string;
}

export async function testGeoPeer(id: string): Promise<GeoTestResult> {
  const res = await fetch(`/api/admin/geo/peers/${encodeURIComponent(id)}/test`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
}

// Phase C — pause / resume preserve the peer's cursor (so a maintenance
// window doesn't force a full outbox replay on resume); flip swaps
// push <-> pull for one-click failover. bidir peers reject the flip.

export async function pauseGeoPeer(id: string): Promise<void> {
  const res = await fetch(`/api/admin/geo/peers/${encodeURIComponent(id)}/pause`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) throw new Error(`${res.status}: ${await res.text()}`);
}

export async function resumeGeoPeer(id: string): Promise<void> {
  const res = await fetch(`/api/admin/geo/peers/${encodeURIComponent(id)}/resume`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) throw new Error(`${res.status}: ${await res.text()}`);
}

export async function flipGeoPeerDirection(
  id: string,
): Promise<{ direction: GeoPeer['direction'] }> {
  const res = await fetch(`/api/admin/geo/peers/${encodeURIComponent(id)}/flip`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
}
